import { useState } from "react";
import axios from "axios";
import { 
  Package, 
  Search, 
  RefreshCw,
  Copy,
  Check,
  Database,
  Globe,
  Server,
  Code,
  Mail,
  Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const popularApps = [
  { name: "Odoo ERP", icon: Database, category: "ERP" },
  { name: "Nginx", icon: Globe, category: "Web Server" },
  { name: "Apache", icon: Globe, category: "Web Server" },
  { name: "MySQL", icon: Database, category: "Database" },
  { name: "PostgreSQL", icon: Database, category: "Database" },
  { name: "MongoDB", icon: Database, category: "Database" },
  { name: "Docker", icon: Package, category: "Container" },
  { name: "Node.js", icon: Code, category: "Runtime" },
  { name: "Python", icon: Code, category: "Runtime" },
  { name: "Redis", icon: Server, category: "Cache" },
  { name: "Postfix", icon: Mail, category: "Mail Server" },
  { name: "Fail2ban", icon: Shield, category: "Security" },
];

export default function AppInstaller() {
  const [appName, setAppName] = useState("");
  const [osType, setOsType] = useState("linux");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const handleGenerateGuide = async (name = appName) => {
    if (!name.trim()) {
      toast.error("Please enter an application name");
      return;
    }

    setLoading(true);
    setGuide(null);
    try {
      const response = await axios.post(`${API}/install-guide`, {
        app_name: name,
        os_type: osType,
        additional_info: additionalInfo || null
      });
      setGuide(response.data);
      toast.success("Installation guide generated");
    } catch (error) {
      toast.error("Failed to generate guide");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="p-6 space-y-6" data-testid="app-installer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight">App Installer</h1>
          <p className="text-zinc-400 text-sm mt-1">AI-generated installation guides for any application</p>
        </div>
        <Select value={osType} onValueChange={setOsType}>
          <SelectTrigger className="w-32 bg-black/50 border-white/10 text-white" data-testid="app-os-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0c0c0e] border-white/10">
            <SelectItem value="linux">Linux</SelectItem>
            <SelectItem value="windows">Windows</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Section */}
        <div className="space-y-4">
          <Card className="bg-[#0c0c0e] border-white/5">
            <CardHeader>
              <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-400" />
                Application Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Application Name</label>
                <Input
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  placeholder="e.g., Odoo ERP, Nginx, Docker"
                  className="bg-black/50 border-white/10 text-white"
                  data-testid="app-name-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Additional Requirements (Optional)</label>
                <Textarea
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  placeholder="e.g., Include SSL setup, use specific version, configure with PostgreSQL..."
                  className="bg-black/50 border-white/10 text-white min-h-[100px]"
                  data-testid="additional-info-input"
                />
              </div>
              <Button
                onClick={() => handleGenerateGuide()}
                disabled={loading || !appName.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white glow-blue"
                data-testid="generate-guide-btn"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Generate Installation Guide
              </Button>
            </CardContent>
          </Card>

          {/* Popular Apps */}
          <Card className="bg-[#0c0c0e] border-white/5">
            <CardHeader>
              <CardTitle className="text-lg font-medium text-white">Popular Applications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {popularApps.map((app) => (
                  <button
                    key={app.name}
                    onClick={() => {
                      setAppName(app.name);
                      handleGenerateGuide(app.name);
                    }}
                    disabled={loading}
                    className="flex items-center gap-2 p-2 text-left bg-black/30 border border-white/5 rounded-lg hover:border-blue-500/30 hover:bg-blue-500/5 transition-colors disabled:opacity-50"
                    data-testid={`popular-app-${app.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <app.icon className="w-4 h-4 text-blue-400" />
                    <div>
                      <p className="text-sm text-white">{app.name}</p>
                      <p className="text-xs text-zinc-500">{app.category}</p>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Guide Section */}
        <div className="lg:col-span-2">
          <Card className="bg-[#0c0c0e] border-white/5 h-full">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                <Search className="w-5 h-5 text-green-400" />
                Installation Guide
                {guide && (
                  <span className="text-sm font-normal text-zinc-400">
                    - {guide.app_name} on {guide.os_type}
                  </span>
                )}
              </CardTitle>
              {guide && (
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                  onClick={() => copyToClipboard(guide.guide, 'full-guide')}
                >
                  {copiedId === 'full-guide' ? (
                    <Check className="w-4 h-4 mr-1 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 mr-1" />
                  )}
                  Copy All
                </Button>
              )}
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[calc(100vh-300px)]">
              {!guide && !loading ? (
                <div className="h-[400px] flex flex-col items-center justify-center text-center">
                  <Package className="w-12 h-12 text-zinc-600 mb-4" />
                  <p className="text-zinc-400 mb-2">Enter an application name or select from popular apps</p>
                  <p className="text-zinc-500 text-sm">Get step-by-step installation instructions</p>
                </div>
              ) : loading ? (
                <div className="h-[400px] flex flex-col items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mb-4" />
                  <p className="text-zinc-400">Generating installation guide...</p>
                </div>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none" data-testid="installation-guide">
                  <ReactMarkdown
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const codeString = String(children).replace(/\n$/, '');
                        if (inline) {
                          return <code className="bg-black/50 px-1.5 py-0.5 rounded text-blue-300 font-mono text-sm" {...props}>{children}</code>;
                        }
                        return (
                          <div className="relative group my-3">
                            <pre className="bg-black/60 border border-white/10 rounded-lg p-4 overflow-x-auto">
                              <code className="font-mono text-sm text-green-300" {...props}>{children}</code>
                            </pre>
                            <button
                              onClick={() => copyToClipboard(codeString, codeString.slice(0, 20))}
                              className="absolute top-2 right-2 p-1.5 rounded bg-white/10 hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              {copiedId === codeString.slice(0, 20) ? (
                                <Check className="w-4 h-4 text-green-400" />
                              ) : (
                                <Copy className="w-4 h-4 text-zinc-400" />
                              )}
                            </button>
                          </div>
                        );
                      },
                      pre: ({ children }) => <>{children}</>,
                      p: ({ children }) => <div className="mb-3 text-zinc-300">{children}</div>,
                      h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-white border-b border-white/10 pb-2">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-xl font-semibold mb-3 mt-6 text-white">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-lg font-medium mb-2 mt-4 text-white">{children}</h3>,
                      ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-2">{children}</ol>,
                      li: ({ children }) => <li className="text-zinc-300">{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-yellow-500/50 pl-4 my-3 text-yellow-200/80 bg-yellow-500/5 py-2 rounded-r">
                          {children}
                        </blockquote>
                      ),
                    }}
                  >
                    {guide.guide}
                  </ReactMarkdown>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
